<template>
  <div class="vehicle-sort-controls d-inline-flex align-center" role="group" aria-label="Sort vehicles">
    <template v-for="(field, index) in fields" :key="field.key">
      <span v-if="index > 0" class="text-medium-emphasis" aria-hidden="true">·</span>
      <button
        type="button"
        class="vehicle-sort-controls__btn"
        :class="{ 'vehicle-sort-controls__btn--active': stateOf(field.key) !== 'none' }"
        :aria-pressed="stateOf(field.key) !== 'none' ? 'true' : 'false'"
        :aria-label="ariaLabel(field)"
        :data-testid="`vehicle-sort-${field.key}`"
        @click="emit('toggle', field.key)"
      >
        <span>{{ field.label }}</span>
        <v-icon
          v-if="stateOf(field.key) !== 'none'"
          :icon="stateOf(field.key) === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'"
          size="14"
        />
      </button>
    </template>
  </div>
</template>

<script lang="ts" setup>
// Presentational Year / Make / Model sort buttons for the Vehicle header (R6). Sort state comes
// from the data table's own header slot; the parent forwards each toggle to the table's native
// toggleSort so paging, single-key sorting and refetch use the existing path.
type SortKey = 'year' | 'make' | 'model'

const props = defineProps<{
  sortBy: ReadonlyArray<{ key: string; order?: 'asc' | 'desc' | boolean }>
}>()

const emit = defineEmits<{
  (e: 'toggle', key: SortKey): void
}>()

const fields: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'year', label: 'Year' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
]

const stateOf = (key: SortKey): 'asc' | 'desc' | 'none' => {
  const active = props.sortBy.find((item) => item.key === key)
  if (!active) return 'none'
  return active.order === 'desc' || active.order === false ? 'desc' : 'asc'
}

const ariaLabel = (field: { key: SortKey; label: string }): string => {
  const state = stateOf(field.key)
  const description =
    state === 'asc' ? 'sorted ascending' : state === 'desc' ? 'sorted descending' : 'not sorted'
  return `Sort by ${field.label}, ${description}`
}
</script>

<style scoped>
.vehicle-sort-controls {
  gap: 2px;
}

.vehicle-sort-controls__btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 4px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.vehicle-sort-controls__btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.vehicle-sort-controls__btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

.vehicle-sort-controls__btn--active {
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
}
</style>
